import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SvgIcon } from '../../components/icons';
import { OverlayLayer } from './OverlayLayer';

/* ───────────────────────────── Tooltip ───────────────────────────────── */

type TooltipProps = {
  label: string;
  placement?: 'top' | 'right';
  children: ReactNode;
};

/**
 * Tooltip.
 *
 * Differences from the production one: the trigger wrapper is `inline-flex`
 * rather than a block `<div>` (which breaks inline layout wherever it is
 * used), and the tip is linked to its trigger with `aria-describedby`, so it
 * is actually announced instead of being decorative markup a screen reader
 * never reaches.
 *
 * Shows on focus as well as hover — essential in the collapsed sidebar rail,
 * where the tooltip is the only thing naming each icon.
 */
export const Tooltip = ({ label, placement = 'top', children }: TooltipProps) => {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number>();
  const id = useId();

  const show = useCallback(() => {
    timerRef.current = window.setTimeout(() => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      setCoords(
        placement === 'right'
          ? { top: rect.top + rect.height / 2 - 12, left: rect.right + 8 }
          : { top: rect.top - 30, left: rect.left },
      );
    }, 120);
  }, [placement]);

  const hide = useCallback(() => {
    window.clearTimeout(timerRef.current);
    setCoords(null);
  }, []);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  return (
    <>
      <span
        ref={wrapRef}
        className="inline-flex"
        aria-describedby={coords ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {coords && (
        <OverlayLayer>
          <span
            id={id}
            role="tooltip"
            className="ds-tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {label}
          </span>
        </OverlayLayer>
      )}
    </>
  );
};

/* ───────────────────────────── Toasts ────────────────────────────────── */

export type ToastTone = 'info' | 'ok' | 'warn' | 'danger';

type Toast = {
  id: number;
  title: string;
  body?: string;
  tone: ToastTone;
};

type ToastApi = {
  push: (t: { title: string; body?: string; tone?: ToastTone }) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, string> = {
  info: 'infoOutlined',
  ok: 'checkCircleOutline',
  warn: 'reportProblemOutlined',
  danger: 'errorOutline',
};

const AUTO_DISMISS_MS = 5000;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback(
    (id: number) => setToasts((list) => list.filter((t) => t.id !== id)),
    [],
  );

  const push = useCallback<ToastApi['push']>(({ title, body, tone = 'info' }) => {
    // eslint-disable-next-line no-plusplus
    const id = nextId.current++;
    setToasts((list) => [...list, { id, title, body, tone }]);
    window.setTimeout(
      () => setToasts((list) => list.filter((t) => t.id !== id)),
      AUTO_DISMISS_MS,
    );
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <OverlayLayer>
          {/* role="status" + aria-live="polite": announced without stealing
              focus or interrupting whatever the user is doing. */}
          <div className="ds-toast-stack" role="status" aria-live="polite">
            {toasts.map((t) => (
              <div key={t.id} className="ds-toast" data-ds-tone={t.tone}>
                <span style={{ marginTop: 1 }}>
                  <SvgIcon name={TONE_ICON[t.tone] as never} size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="ds-toast-title">{t.title}</div>
                  {t.body && <div className="ds-toast-body">{t.body}</div>}
                </div>
                {/* A real button, unlike the production Snackbar's bare icon
                    with an onClick — that one is unreachable by keyboard and
                    has no accessible name. */}
                <button
                  type="button"
                  className="ds-icon-btn"
                  style={{ width: 20, height: 20 }}
                  aria-label={`Dismiss: ${t.title}`}
                  onClick={() => dismiss(t.id)}
                >
                  <SvgIcon name="close" size={13} />
                </button>
              </div>
            ))}
          </div>
        </OverlayLayer>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
};
